import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'gatsby'
import { usePlayer } from '../context/player-context'

function formatTime(value) {
  if (!value || Number.isNaN(value)) return '0:00'

  const minutes = Math.floor(value / 60)
  const seconds = Math.floor(value % 60)

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export default function GlobalAudioPlayer() {
  const {
    audioRef,
    currentEpisode,
    isPlaying,
    playRequest,
    togglePlay,
    closePlayer,
    setIsPlaying,
  } = usePlayer()

  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

    useEffect(() => {
        const audio = audioRef.current

        if (!audio || !currentEpisode?.audio) return

        audio.load()
        setCurrentTime(0)

        audio.play()
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false))
    }, [
        audioRef,
        currentEpisode?.audio,
        playRequest,
        setIsPlaying,
    ])

  const progress = useMemo(() => {
    if (!duration) return 0

    return (currentTime / duration) * 100
  }, [currentTime, duration])

  const handleSeek = (event) => {
    const audio = audioRef.current

    if (!audio || !duration) return

    const nextValue = Number(event.target.value)
    const nextTime = (nextValue / 100) * duration

    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }

  if (!currentEpisode) {
    return null
  }

  return (
    <aside className="global_audio_player" aria-label="Аудиоплеер">
      <div className="global_audio_player_inner">
        <img
          className="global_audio_player_cover"
          src={currentEpisode.cover}
          alt=""
        />

        <button
          className="global_audio_player_button"
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
        >
          <span className="global_audio_player_button_icon" aria-hidden="true">
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M8 6.5v11M16 6.5v11"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M8.5 6.8v10.4c0 .8.9 1.3 1.6.9l8.1-5.2c.6-.4.6-1.3 0-1.7L10.1 6c-.7-.5-1.6 0-1.6.8Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </span>
        </button>

        <div className="global_audio_player_content">
          {currentEpisode.slug ? (
            <Link className="global_audio_player_title" to={currentEpisode.slug}>
              {currentEpisode.title}
            </Link>
          ) : (
            <span className="global_audio_player_title">
              {currentEpisode.title}
            </span>
          )}

          <div className="global_audio_player_progress">
            <span className="global_audio_player_time">{formatTime(currentTime)}</span>

            <input
              className="global_audio_player_range"
              type="range"
              min="0"
              max="100"
              step="0.1"
              value={progress}
              onChange={handleSeek}
              aria-label="Позиция воспроизведения"
            />

            <span className="global_audio_player_time">
              {duration ? formatTime(duration) : currentEpisode.duration || '0:00'}
            </span>
          </div>
        </div>

        <button
          className="global_audio_player_close"
          type="button"
          onClick={closePlayer}
          aria-label="Закрыть плеер"
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="m7 7 10 10M17 7 7 17"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio
          ref={audioRef}
          src={currentEpisode.audio}
          preload="metadata"
          onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
          onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        />
      </div>
    </aside>
  )
}